import {
  accountTypeWithBorrower,
  FieldOrigin,
  InputType,
  isCustomOrigin,
  NewInputTextOptions,
  NewInputTypeOptions,
} from 'config'
import {
  CHANNEL_VISIBLE_LOGIC_FIELD_KEY,
  LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN,
} from 'config/loan.input.visibility.type'
import { store } from 'reducers'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { spanOptions } from 'utils/loanFieldSpan'

export { spanOptions } from 'utils/loanFieldSpan'

/** Extra Page Origin for per-choice visible logic (loan-level fields: Channel, …). */
export const loanContextVisibleLogicAdditionalFieldOrigins = () => ({
  [LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN]: {
    title: 'Loan Context',
    inputs: {
      [CHANNEL_VISIBLE_LOGIC_FIELD_KEY]: {
        inputType: 'select' as const,
        title: 'Channel',
        options: getLoanTypeMapForSelect(),
      },
    },
  },
})

/** @deprecated Use loanContextVisibleLogicAdditionalFieldOrigins */
export const channelVisibleLogicAdditionalFieldOrigins = loanContextVisibleLogicAdditionalFieldOrigins

export const isVisibleAccountTypesFieldOrigins = (fieldOrigin: FieldOrigin) =>
  [
    FieldOrigin.LoanStructure,
    FieldOrigin.BorrowerInformation,
    FieldOrigin.PropertyInformation,
    FieldOrigin.EmploymentAndIncome,
    FieldOrigin.AssetLiability,
    FieldOrigin.DeclarationsHMDA,
    FieldOrigin.TrackRecord,
    'sign',
  ].includes(fieldOrigin) || isCustomOrigin(fieldOrigin)

export const invisibleFieldsForLinkedField = [
  'inputType',
  'type',
  'options',
  'required',
  'coborrowerRequired',
  'linkToParty',
]

export const titleLogicFieldOrigins = (fieldOrigin: FieldOrigin) => ['sign'].includes(fieldOrigin)
export const lockByFieldOrigins = (fieldOrigin: FieldOrigin) => ![FieldOrigin.LoanStructure].includes(fieldOrigin)
export const viewOnlyLogicFieldOrigins = (fieldOrigin: FieldOrigin) =>
  [FieldOrigin.LoanStructure, FieldOrigin.BorrowerInformation, FieldOrigin.PropertyInformation].includes(fieldOrigin) ||
  isCustomOrigin(fieldOrigin)

export const editPermissionFieldOrigins = viewOnlyLogicFieldOrigins

export const editableSelectFields = ['monthsReserve']

export const defaultInputs = (): Record<string, InputType> => {
  const { loanGlobalStatus } = store.getState()

  return {
    visibleAccountTypes: {
      inputType: 'multiselect',
      title: 'Visible by Account Type',
      options: {
        self_creator: '[Loan] Creator',
        ...accountTypeWithBorrower(),
      },
      defaultSelected: true,
      value: {},
      sort: false,
    },
    visibleLoanTypes: {
      inputType: 'multiselect',
      title: 'Visible by Channel',
      options: getLoanTypeMapForSelect(),
      defaultSelected: true,
      value: {},
      sort: false,
    },
    section: {
      inputType: 'select',
      title: 'Section',
      required: false,
      hasDefaultOption: true,
      options: {},
    },
    title: {
      inputType: 'text',
      title: 'Title',
      required: false,
      value: true,
    },
    inputType: {
      inputType: 'Select',
      title: 'Input Component Type',
      options: NewInputTypeOptions,
      required: true,
      hasDefaultOption: true,
      visible: false,
    },
    type: {
      inputType: 'select',
      title: 'Input Format',
      options: NewInputTextOptions,
      required: true,
      hasDefaultOption: true,
      allowDefaultOption: true,
      visible: false,
    },
    defaultValue: {
      inputType: 'text',
      title: 'Default Value',
      required: true,
      value: '',
      visible: false,
    },
    options: {
      inputType: 'SelectOptionInput',
      title: 'Select Options',
      value: [],
      required: true,
      visible: false,
    },
    tooltip: {
      inputType: 'textarea',
      title: 'Tooltip',
      rows: 3,
      required: false,
      prefix: '',
      value: '',
    },
    byteproKey: {
      inputType: 'text',
      title: 'Bytepro Key',
      required: false,
      value: '',
      visible: true,
    },
    required: {
      inputType: 'ToggleButton',
      title: 'Required',
      required: false,
      value: true,
    },
    coborrowerRequired: {
      inputType: 'ToggleButton',
      title: 'Coborrower Required',
      required: false,
      value: true,
    },
    visibleLogic: {
      inputType: 'visibleLogic',
      title: '',
      required: false,
      value: [],
      options: {
        hasNotVisible: true,
      },
    },
    lockByAccountTypes: {
      inputType: 'multiselect',
      title: 'Lock by Account Type',
      options: accountTypeWithBorrower(),
      defaultSelected: false,
      value: {},
      sort: false,
    },
    lockByLoanStatus: {
      inputType: 'multiselect',
      title: 'Lock by Loan Status',
      options: loanGlobalStatus,
      defaultSelected: false,
      value: {},
      sort: false,
    },
    linkToParty: {
      inputType: 'Custom',
      title: '',
      required: false,
      value: {},
      visible: false,
    },
    actionType: {
      inputType: 'select',
      title: 'Loan Action Category',
      options: {},
      hasDefaultOption: true,
      required: true,
      visible: false,
    },
    buttonStyle: {
      inputType: 'select',
      title: 'Button Style',
      options: {
        primary: 'Primary',
        outline: 'Outline',
        link: 'Link',
      },
      hasDefaultOption: false,
      required: false,
      visible: false,
    },
    clickMode: {
      inputType: 'select',
      title: 'Clickable',
      options: {
        always: 'Always',
        once: 'Once',
      },
      hasDefaultOption: false,
      required: false,
      visible: false,
    },
    span: {
      inputType: 'select',
      title: 'Span',
      options: spanOptions,
      hasDefaultOption: false,
      value: 1,
      visible: false,
    },
    viewOnlyLogic: {
      inputType: 'Custom',
      title: 'View only Conditions',
      required: false,
      value: [],
    },
    editPermissionSection: {
      inputType: 'section',
      title: 'Edit Permission',
      span: 'full',
      visible: false,
    },
    editPermissionAccountTypes: {
      inputType: 'multiselect',
      title: 'Account Type',
      tooltip:
        'Selected account types can edit this field. Unselected types are view-only. Existing disabled/read-only rules still apply first.',
      options: accountTypeWithBorrower(),
      defaultSelected: true,
      value: {},
      sort: false,
      visible: false,
    },
  }
}

export const inferActionTypeFromFieldOrigin = (fieldOrigin: string): string => {
  if (isCustomOrigin(fieldOrigin)) return 'loanOrdinary'
  switch (fieldOrigin) {
    case 'sign':
    case FieldOrigin.BorrowerInformation:
    case FieldOrigin.PropertyInformation:
    case FieldOrigin.AssetLiability:
    case FieldOrigin.AdditionalMember:
    case FieldOrigin.DeclarationsHMDA:
      return 'loanSubmission'
    case FieldOrigin.LoanStructure:
      return 'loanStructure'
    case FieldOrigin.TrackRecord:
      return 'loanApplication'
    default:
      return 'loanOrdinary'
  }
}

export enum InterestOptions {
  NonInterestBased = 0,
  InterestBased = 1,
}

export const InterestOptionsMap: Record<InterestOptions, string> = {
  [InterestOptions.NonInterestBased]: 'Non Interest Based',
  [InterestOptions.InterestBased]: 'Interest Based',
}
